import { useCallback, useState } from "react"

import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import Skeleton from "$/components/Skeleton/Skeleton"
import { axiosClient } from "$/http"
import { isEmptyString } from "$/utils/gates"
import { getErrorMessages } from "$/utils/get-error-messages"

import { type TUserState, useUser } from "@/3514/hooks"
import { type TCoachInfoQuery, type TCoachRatingQuery, useCoachInfoQuery, useCoachRatingQuery } from "@/3514/queries"
import { EProductTypeInternalName, EUserRole } from "@/3514/types"
import Urls from "@/api/urls"
import { CoachRatingAskedFrom, CoachRatingSource } from "@/constants"
import { type TGetValueFromTranslatedObjectFunction, useGetValueFromTranslatedObject } from "@/hooks"
import Coach from "@/models/coach"
import { WarningMessage } from "@/shared/WarningMessage"
import Avatar from "@/shared/avatar/Avatar"
import DividerLine from "@/shared/divider/DividerLine"
import TextInputBox from "@/shared/input/TextInputBox"
import StarRatings from "@/shared/rating/StarRatings"

import QuestionRow from "../diagnostic-question/QuestionRow"

import "./styles.scss"

type Props = {
    askedFrom: CoachRatingSource
    participantId: number
    coachSessionId?: number
    coachData?: Coach
    errorMessage?: string
    sendFreeResponse?: boolean
    showDividerLine?: boolean
    orderNumber?: number | null
    textInputBgVariant?: "dark" | "light"
    onStarSelect?: (data: any) => void
    saveCoachRating?: (coachRatingId: number, freeResponse: string) => void
    onSubmitCoachRating?: (source: CoachRatingSource) => void
}

/**
 * @deprecated uses legacy stuff, can't and shouldn't be supported; rewrite A$AP
 */
const CoachRating = ({
    askedFrom,
    participantId,
    coachSessionId,
    coachData,
    errorMessage = "",
    sendFreeResponse = true,
    showDividerLine = true,
    orderNumber = 1,
    textInputBgVariant = "dark",
    onSubmitCoachRating,
    onStarSelect,
    saveCoachRating
}: Props) => {
    const { data, isLoading: isLoadingRating }: TCoachRatingQuery = useCoachRatingQuery({
        enabled: true,
        participantId,
        type: askedFrom
    })

    const { user }: TUserState<EUserRole.Participant> = useUser<EUserRole.Participant>()

    const { t } = useTranslation()
    const { data: coach, isFetching: isLoadingCoach }: TCoachInfoQuery = useCoachInfoQuery({
        participantId,
        enabled: true
    })
    const [selectedStar, setSelectedStar] = useState(0)
    const [coachRating, setCoachRating] = useState(null)
    const [error, setError] = useState(null)
    const { register, getValues } = useForm()

    const getValueFromTranslatedObject: TGetValueFromTranslatedObjectFunction = useGetValueFromTranslatedObject()

    const sendRating = async star => {
        const submitData = {
            participant: user.id,
            coach: coachData?.id ?? coach?.id,
            rating: star,
            question: data?.id,
            free_response: coachRating?.free_response ?? ""
        }

        if (askedFrom === CoachRatingAskedFrom.POST_MODULE_REFLECTION_POPUP) {
            Object.assign(submitData, { coaching_session: coachSessionId })
        }

        if (onStarSelect) {
            const { oneStarQuestion, twoStarQuestion, threeStarQuestion, fourStarQuestion, fiveStarQuestion } =
                data.questions

            const responseByStar = {
                1: oneStarQuestion,
                2: twoStarQuestion,
                3: threeStarQuestion,
                4: fourStarQuestion,
                5: fiveStarQuestion
            }

            onStarSelect(submitData)
            setCoachRating({ rating_question: getValueFromTranslatedObject(responseByStar[star]), ...submitData })
            return
        }

        try {
            const { data: response } = await axiosClient.post(Urls.submitRating(), submitData)
            setCoachRating(response)
            if (saveCoachRating) {
                const freeResponse = getValues("free_response_prompt")
                saveCoachRating(response.id, !isEmptyString(freeResponse) ? freeResponse : "")
            }
            if (onSubmitCoachRating) {
                onSubmitCoachRating(askedFrom)
            }
        } catch (error) {
            setError(getErrorMessages(error))
            console.log(error)
        }
    }

    const onSelectStar = (star: number) => {
        setSelectedStar(star)
        sendRating(star)
    }

    const onBlurFreeResponseField = useCallback(async () => {
        const freeResponse = getValues("free_response_prompt")
        if (!freeResponse) {
            return
        }

        if (saveCoachRating) {
            saveCoachRating(coachRating.id, freeResponse)
        }

        if (sendFreeResponse) {
            try {
                await axiosClient.patch(Urls.submitRating(coachRating.id), { free_response: freeResponse })
            } catch (error) {
                setError(getErrorMessages(error))
            }
        }
    }, [coachRating])

    const isLoading = isLoadingCoach && isLoadingRating

    const showRating: boolean = user.productType !== EProductTypeInternalName.SpotCoaching

    return (
        <>
            {showRating ? (
                <div className="coach-rating" data-testid="coach-rating">
                    {isLoading ? (
                        <Skeleton rows={2} height={40} />
                    ) : (
                        <>
                            <QuestionRow
                                question={getValueFromTranslatedObject(data?.questions?.mainQuestion)}
                                order={orderNumber}
                                isRequired={data?.isRequired}
                            />
                            <div className="d-flex align-items-center flex-wrap">
                                <Avatar
                                    url={coachData?.photo ?? coach?.photo}
                                    width={40}
                                    height={40}
                                    alt="coach-photo"
                                />
                                <p className="m-0 text-gray-100 mx-2">{coachData?.firstName ?? coach?.firstName}:</p>
                                <StarRatings selectedStar={selectedStar} onSelect={onSelectStar} />
                            </div>
                            <WarningMessage message={error || errorMessage} />
                            {coachRating ? (
                                <div className="px-0 px-md-3">
                                    <TextInputBox
                                        name="free_response_prompt"
                                        register={register}
                                        backgroudVariant={textInputBgVariant}
                                        labelText={coachRating?.rating_question}
                                        placeholder={data?.freeResponsePrompt || t("Start typing...")}
                                        onBlur={onBlurFreeResponseField}
                                        className="mt-[30px] text-gray-100 coach-rating-input-box"
                                    />
                                </div>
                            ) : null}
                            {showDividerLine && <DividerLine className="divider-coach show-mobile" />}
                        </>
                    )}
                </div>
            ) : null}
        </>
    )
}

export default CoachRating
